import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["banner", "analytics", "adblockWarning", "analyticsText"]

  connect() {
    // Check for ad blockers
    this.checkForAdBlockers();
    
    // Show banner if no consent set
    if (!this.hasCookieConsentSaved()) {
      this.bannerTarget.classList.remove('hidden')
    }
  }
  
  // Simple check for ad blockers
  checkForAdBlockers() {
    const testScript = document.createElement('script');
    testScript.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js';
    testScript.setAttribute('data-ad-client', 'test');
    testScript.onerror = () => {
      console.log('Ad blocker detected');
      window.adBlockerDetected = true;
      
      // Show adblock warning if we have the target
      if (this.hasAdblockWarningTarget) {
        this.adblockWarningTarget.classList.remove('hidden');
      }
      
      // If analytics inputs exist, disable them
      if (this.hasAnalyticsTarget) {
        this.analyticsTarget.disabled = true;
        this.analyticsTarget.checked = false;
        
        // Add explanatory text
        if (this.hasAnalyticsTextTarget) {
          this.analyticsTextTarget.innerHTML = 'Geblokkeerd door adblocker <span class="text-yellow-400">(niet beschikbaar)</span>';
        }
      }
      
      // Inform the rest of the application
      if (typeof window.handleAdBlockDetection === 'function') {
        window.handleAdBlockDetection();
      }
    };
    
    // Remove test script after a small delay
    testScript.addEventListener('load', () => {
      setTimeout(() => {
        testScript.remove();
      }, 100);
    });
    
    // Add script to document
    document.head.appendChild(testScript);
  }

  accept() {
    // Save scroll position before reload
    localStorage.setItem('scrollPosition', window.scrollY.toString())
    
    // Set consent and update UI
    this.setConsent(true)
    this.bannerTarget.classList.add('hidden')
    
    // Update analytics consent, but only if not blocked
    if (!window.adBlockerDetected) {
      this.dispatchConsentEvent(true)
    } else {
      console.log("Analytics consent ignored due to ad blocker");
    }
    
    // Reload the page to ensure settings are applied
    window.location.reload()
  }

  decline() {
    // Save scroll position before reload
    localStorage.setItem('scrollPosition', window.scrollY.toString())
    
    // Set consent and update UI
    this.setConsent(false)
    this.bannerTarget.classList.add('hidden')
    
    // Update analytics consent
    this.dispatchConsentEvent(false)
    
    // Reload the page to ensure gtag is not loaded
    window.location.reload()
  }

  hasCookieConsentSaved() {
    return localStorage.getItem('cookieConsent') !== null
  }

  hasConsent() {
    return localStorage.getItem('cookieConsent') === 'accepted'
  }

  setConsent(accepted) {
    const consentValue = accepted ? 'accepted' : 'declined'
    localStorage.setItem('cookieConsent', consentValue)
    
    // Also store analytics consent specifically
    localStorage.setItem('analyticsConsent', accepted ? 'granted' : 'denied')
    
    // Set server-side cookie with secure flags
    const cookieOptions = `path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`
    document.cookie = `cookie_consent=${consentValue}; ${cookieOptions}`
  }

  dispatchConsentEvent(consent) {
    // Dispatch a custom event that our Google Analytics code listens for
    window.dispatchEvent(
      new CustomEvent('cookieConsentChanged', {
        detail: { consent }
      })
    )

    // If consent is granted, try to initialize or update GA
    if (consent) {
      this.initializeAnalytics()
    }

    // Update Google's consent mode directly if gtag is available
    if (window.gtag) {
      const consentStatus = consent ? 'granted' : 'denied';
      gtag('consent', 'update', {
        'ad_storage': consentStatus,
        'analytics_storage': consentStatus,
        'functionality_storage': consentStatus,
        'personalization_storage': consentStatus,
        'security_storage': 'granted',
        'ad_user_data': consentStatus,
        'ad_personalization': consentStatus
      })
    }
  }

  // Initialize Google Analytics manually if needed
  initializeAnalytics() {
    // If gtag doesn't exist but consent is granted, set it up
    if (!window.gtag && this.hasConsent() && !window.gaBlocked) {
      console.log("Initializing Google Analytics manually");
      
      try {
        window.dataLayer = window.dataLayer || [];
        window.gtag = function() {
          window.dataLayer.push(arguments);
        };
        
        // Configure gtag
        gtag('js', new Date());
        gtag('config', 'G-TN4SX0LBHJ');
        
        // Add error handler for blocked scripts
        window.addEventListener('error', function(e) {
          if (e.target.src && e.target.src.includes('googletagmanager.com')) {
            console.log('Google Analytics script was blocked by browser settings or extension');
            window.gaBlocked = true;
          }
        }, true);
        
        // Load the script using more reliable method
        (function() {
          var ga = document.createElement('script');
          ga.type = 'text/javascript';
          ga.async = true;
          ga.src = 'https://www.googletagmanager.com/gtag/js?id=G-TN4SX0LBHJ';
          ga.onerror = function() {
            console.log('Google Analytics script failed to load or was blocked');
            window.gaBlocked = true;
          };
          var s = document.getElementsByTagName('script')[0];
          s.parentNode.insertBefore(ga, s);
          console.log("Google Analytics script insertion attempted");
        })();
      } catch (err) {
        console.error('Error during manual initialization of Google Analytics:', err);
        window.gaBlocked = true;
      }
    }
  }
}

// Add this event listener to restore the scroll position after reload
window.addEventListener('load', () => {
  const scrollPosition = localStorage.getItem('scrollPosition')
  if (scrollPosition) {
    window.scrollTo(0, parseInt(scrollPosition, 10))
    localStorage.removeItem('scrollPosition') // Clean up
  }
}) 